<template>
  <div class="system-settings-page">
    <div class="header-row">
      <div>
        <h2>系统设置</h2>
        <p>{{ isSuperAdmin ? "配置订单录入规则和配送员注册二维码。" : "生成配送员注册二维码。" }}</p>
      </div>
      <el-button v-if="isSuperAdmin" type="primary" :loading="saving" @click="saveSettings">保存设置</el-button>
    </div>

    <div v-if="isSuperAdmin" class="brand-card settings-card">
      <el-form :model="form" label-width="190px">
        <el-form-item label="收件人号码有效天数">
          <div class="setting-control">
            <el-input-number
              v-model="form.recipient_phone_unique_days"
              :min="0"
              :max="3650"
              :precision="0"
              :step="1"
              controls-position="right"
            />
            <span class="unit-text">天</span>
          </div>
          <div class="form-tip">
            设置为 90 时，新建订单会校验最近 90 天内是否存在相同收件人号码；存在则禁止录入。设置为 0 表示不校验。
          </div>
        </el-form-item>
        <el-form-item label="登录页Logo">
          <div class="logo-setting">
            <div class="logo-preview">
              <img v-if="form.login_logo_url" :src="form.login_logo_url" alt="登录页Logo" />
              <span v-else>暂无Logo</span>
            </div>
            <div class="logo-actions">
              <el-button size="small" type="primary" plain :loading="uploadingLogo" @click="triggerLogoUpload">上传/替换</el-button>
              <el-button v-if="form.login_logo_url" size="small" plain @click="clearLogo">清除</el-button>
              <input ref="logoFileInput" type="file" accept="image/*" style="display: none;" @change="handleLogoFileChange" />
            </div>
          </div>
          <div class="form-tip">上传后保存设置，登录页面会展示该Logo。</div>
        </el-form-item>
        <el-form-item label="订单超时时间">
          <div class="setting-control">
            <el-input-number
              v-model="form.order_timeout_minutes"
              :min="0"
              :max="10080"
              :precision="0"
              :step="10"
              controls-position="right"
            />
            <span class="unit-text">分钟</span>
          </div>
          <div class="form-tip">按派单时间开始计算，未完成、未失败的订单超过该时长后在订单列表标记为超时。设置为 0 表示不标记超时。</div>
        </el-form-item>
        <el-form-item label="登录短信校验管理员">
          <el-select v-model="form.admin_sms_verify_user_ids" multiple filterable clearable style="width: 100%;" placeholder="请选择需要短信校验的管理员">
            <el-option v-for="user in adminUsers" :key="user.id" :label="formatAdminLabel(user)" :value="user.id" />
          </el-select>
          <div class="form-tip">仅对 admin 角色账号生效；未选中的管理员账号登录后台不再强制短信二次校验。</div>
        </el-form-item>
        <el-form-item label="配送短信权限管理员">
          <el-select v-model="form.delivery_sms_admin_ids" multiple filterable clearable style="width: 100%;" placeholder="请选择可使用配送短信的管理员">
            <el-option v-for="user in adminUsers" :key="user.id" :label="formatAdminLabel(user)" :value="user.id" />
          </el-select>
          <div class="form-tip">被授权的管理员创建订单时可勾选配送短信；超级管理员默认可使用。</div>
        </el-form-item>
      </el-form>
    </div>

    <div class="brand-card settings-card">
      <div class="card-title-row">
        <div>
          <h3>配送员注册二维码</h3>
          <p>配送员扫码后打开小程序注册页，注册成功后自动归属到对应管理员。</p>
        </div>
        <el-button type="primary" :loading="qrLoading" @click="generateQrcode">生成二维码</el-button>
      </div>
      <el-form label-width="190px">
        <el-form-item v-if="isSuperAdmin" label="归属管理员">
          <el-select v-model="qrAdminId" filterable clearable style="width: 100%;" placeholder="请选择配送员注册后归属的管理员">
            <el-option v-for="user in adminUsers" :key="user.id" :label="formatAdminLabel(user)" :value="user.id" />
          </el-select>
          <div class="form-tip">超级管理员生成二维码时需要选择归属管理员。</div>
        </el-form-item>
        <el-form-item v-else label="归属管理员">
          <div class="readonly-text">{{ formatAdminLabel(currentUser) }}</div>
          <div class="form-tip">使用该二维码注册的配送员会自动归属到当前管理员。</div>
        </el-form-item>
      </el-form>
      <div v-if="qrcodeInfo.qrcode_base64" class="qrcode-panel">
        <div class="qrcode-box">
          <img :src="qrcodeInfo.qrcode_base64" alt="配送员注册二维码" />
        </div>
        <div class="qrcode-meta">
          <div><span>归属管理员</span><strong>{{ qrcodeInfo.admin_name || qrcodeInfo.admin_username }}</strong></div>
          <div><span>小程序页面</span><strong>{{ qrcodeInfo.page }}</strong></div>
          <div><span>邀请码</span><strong>{{ qrcodeInfo.scene }}</strong></div>
        </div>
      </div>
      <div v-else class="empty-qrcode">点击“生成二维码”后展示配送员注册二维码。</div>
    </div>
  </div>
</template>

<script>
import { fetchSystemSettings, fetchUsers, generateCourierRegisterQrcode, updateSystemSettings, uploadFile } from "../api/orders";
import { getStoredUser } from "../utils/auth";

export default {
  data() {
    return {
      saving: false,
      uploadingLogo: false,
      qrLoading: false,
      currentUser: getStoredUser(),
      form: {
        recipient_phone_unique_days: 0,
        login_logo_url: "",
        order_timeout_minutes: 0,
        admin_sms_verify_user_ids: [],
        delivery_sms_admin_ids: [],
      },
      adminUsers: [],
      qrAdminId: null,
      qrcodeInfo: {},
    };
  },
  computed: {
    isSuperAdmin() {
      return this.currentUser.role === "super_admin";
    },
  },
  mounted() {
    if (this.isSuperAdmin) {
      this.loadAdminUsers();
      this.loadSettings();
    }
  },
  methods: {
    async loadAdminUsers() {
      const { data } = await fetchUsers({ role: "admin", page: 1, page_size: 1000, is_active: true });
      this.adminUsers = data.items || [];
    },
    async loadSettings() {
      const { data } = await fetchSystemSettings();
      this.form = {
        recipient_phone_unique_days: data.recipient_phone_unique_days || 0,
        login_logo_url: data.login_logo_url || "",
        order_timeout_minutes: data.order_timeout_minutes || 0,
        admin_sms_verify_user_ids: data.admin_sms_verify_user_ids || [],
        delivery_sms_admin_ids: data.delivery_sms_admin_ids || [],
      };
    },
    formatAdminLabel(user) {
      return user.name ? `${user.name}（${user.username}）` : user.username;
    },
    async generateQrcode() {
      if (this.isSuperAdmin && !this.qrAdminId) {
        this.$message.warning("请选择归属管理员");
        return;
      }
      this.qrLoading = true;
      try {
        const { data } = await generateCourierRegisterQrcode({
          admin_id: this.isSuperAdmin ? this.qrAdminId : undefined,
        });
        this.qrcodeInfo = data || {};
        this.$message.success("配送员注册二维码已生成");
      } finally {
        this.qrLoading = false;
      }
    },
    triggerLogoUpload() {
      if (this.$refs.logoFileInput) {
        this.$refs.logoFileInput.value = "";
        this.$refs.logoFileInput.click();
      }
    },
    clearLogo() {
      this.form.login_logo_url = "";
    },
    async handleLogoFileChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.uploadingLogo = true;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await uploadFile(formData);
        this.form.login_logo_url = data.file_url || "";
      } finally {
        this.uploadingLogo = false;
      }
    },
    async saveSettings() {
      this.saving = true;
      try {
        const { data } = await updateSystemSettings({
          recipient_phone_unique_days: this.form.recipient_phone_unique_days || 0,
          login_logo_url: this.form.login_logo_url || "",
          order_timeout_minutes: this.form.order_timeout_minutes || 0,
          admin_sms_verify_user_ids: this.form.admin_sms_verify_user_ids || [],
          delivery_sms_admin_ids: this.form.delivery_sms_admin_ids || [],
        });
        this.form = {
          recipient_phone_unique_days: data.recipient_phone_unique_days || 0,
          login_logo_url: data.login_logo_url || "",
          order_timeout_minutes: data.order_timeout_minutes || 0,
          admin_sms_verify_user_ids: data.admin_sms_verify_user_ids || [],
          delivery_sms_admin_ids: data.delivery_sms_admin_ids || [],
        };
        this.$message.success("系统设置已保存");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.settings-card {
  padding: 24px;
  margin-bottom: 18px;
}

.card-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.card-title-row h3 {
  margin: 0 0 6px;
  color: #173c89;
  font-size: 18px;
}

.card-title-row p {
  margin: 0;
  color: #60718f;
  font-size: 13px;
}

.setting-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.unit-text {
  color: #60718f;
}

.form-tip {
  margin-top: 8px;
  color: #8b97aa;
  font-size: 13px;
  line-height: 1.6;
}

.logo-setting {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-preview {
  width: 180px;
  height: 72px;
  border: 1px solid #dbe5f6;
  border-radius: 8px;
  background: #f8fbff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #8b97aa;
  font-size: 13px;
}

.logo-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.logo-actions {
  display: flex;
  gap: 8px;
}

.readonly-text {
  min-height: 40px;
  line-height: 40px;
  color: #183153;
  font-weight: 600;
}

.qrcode-panel {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 20px;
  border-radius: 12px;
  background: #f8fbff;
}

.qrcode-box {
  width: 220px;
  height: 220px;
  flex: 0 0 220px;
  padding: 10px;
  border: 1px solid #dbe5f6;
  border-radius: 12px;
  background: #fff;
  box-sizing: border-box;
}

.qrcode-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.qrcode-meta {
  display: grid;
  gap: 14px;
  min-width: 0;
  padding-top: 8px;
}

.qrcode-meta div {
  display: grid;
  gap: 4px;
}

.qrcode-meta span {
  color: #8b97aa;
  font-size: 13px;
}

.qrcode-meta strong {
  color: #183153;
  font-size: 14px;
  word-break: break-all;
}

.empty-qrcode {
  padding: 26px;
  border-radius: 12px;
  background: #f8fbff;
  color: #8b97aa;
  text-align: center;
}
</style>
